import { defaultRuleRegistry } from './rules/registry';
import type {
  Diagnostic,
  RuleEvaluationContext,
  ValidationContext,
} from './types';
import { computeResolvedSubroutines } from './util/computeResolvedSubroutines';
import { normalizeFromRemoteControlUI } from './util/normalizers';
import { runRules } from './util/ruleRunner';
import { runValidation } from './util/runValidation';

// Public surface: primary function + types
export * from './types';
export { runCommonRoutineRules } from './rules/commonRules';
export type { StepLike } from './rules/commonRules';
export { describeDiagnostics } from './util/describeDiagnostics';
export { isExamplePlaceholder } from './util/examplePlaceholder';
export {
  getInvalidLocateSkillFields,
  getMissingLocateSkillConfig,
} from './util/locateSkill';
export type { MissingLocateSkillConfig } from './util/locateSkill';

/**
 * Every diagnostic for one step, errors and warnings together.
 *
 * Step-scoped on purpose: a caller that wants one step's findings should not
 * pay for the whole routine, and should not have to filter a whole-routine
 * result to get there. `runRules` already supports the scope; this exposes it.
 *
 * Unlike `precheckForExecution` this makes no judgement about playability —
 * that is a property of the routine, not of one step — so it returns the
 * diagnostics and lets the caller decide what to do with each tier.
 */
export function validateStep(
  routine: unknown,
  stepId: string,
  options: { ctx?: ValidationContext } = {},
): Diagnostic[] {
  const ctx: ValidationContext = options.ctx ?? {};

  const withRaw: RuleEvaluationContext = { ...ctx, rawRoutine: routine };

  const ruleCtx =
    ctx.subroutines && ctx.subroutines.size > 0
      ? {
          ...withRaw,
          resolvedSubroutines: computeResolvedSubroutines(ctx.subroutines, ctx),
        }
      : withRaw;

  const routineView = normalizeFromRemoteControlUI(
    routine as Parameters<typeof normalizeFromRemoteControlUI>[0],
  );

  const step = routineView.steps.find((s) => s.id === stepId);

  if (!step) return [];

  return runRules(defaultRuleRegistry, routineView, ruleCtx, {
    stepId,
    stepKind: step.stepKind,
  });
}

export function precheckForExecution(
  routine: unknown,
  options: { ctx?: ValidationContext } = {},
):
  | { ok: true; warnings: Diagnostic[] }
  | { ok: false; errors: Diagnostic[]; warnings: Diagnostic[] } {
  const ctx: ValidationContext = options.ctx ?? {};

  const withRaw: RuleEvaluationContext = { ...ctx, rawRoutine: routine };

  const ruleCtx =
    ctx.subroutines && ctx.subroutines.size > 0
      ? {
          ...withRaw,
          resolvedSubroutines: computeResolvedSubroutines(ctx.subroutines, ctx),
        }
      : withRaw;

  return runValidation(routine, ruleCtx);
}
